import React, { useEffect, useState } from 'react';
import MuiAlert, { AlertColor } from '@mui/material/Alert';
import { getAvailableLeagues } from '../../../api/leagues';
import {createNewGroup, getCompetitionPlaces, getLevels, updateGroup} from '../../../api/settings';
import { createPreSignedUrl, deleteImage, getImagesList } from '../../../api/files';
import { ImageType } from '../../mediaLibrary';
import {
  Avatar,
  Button,
  Dialog,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  FormControlLabel, Grid, InputLabel, MenuItem, Select, Snackbar, Switch,
  TextField
} from '@mui/material';
import ImageTwoToneIcon from '@mui/icons-material/ImageTwoTone';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';

const NewCompetitionsDialog = (
  { isOpen, handleCloseNewGroupDialog, selectedCompetition, handleOnUpdate }:
    { isOpen: boolean; handleCloseNewGroupDialog(isOpenSnackbar?: boolean): void; selectedCompetition: any; handleOnUpdate: () => {} }
  ) => {
  const [newGroupForm, setNewGroupForm] = useState<any>(selectedCompetition || {
    name: '',
    description: '',
    isPaid: false,
    price: 0,
    levelId: '',
    imageId: null,
    imageDisabledId: null,
    leagues: []
  });
  const [selectedLeagues, setSelectedLeagues] = useState<any>([]);
  const [isValidFormData, setIsValidFormData] = useState(false);
  const [leaguesList, setLeaguesList] = useState<any>([]);
  const [openSnackbar, setOpenSnackbar] = useState<{isOpen: boolean, alertType: AlertColor, message: string}>({
    isOpen: false,
    alertType: 'success',
    message: ''
  });
  const [levels, setLevels] = useState([]);
  const [imagePreview, setImagePreview] = useState('');
  const [disabledImagePreview, setDisabledImagePreview] = useState('');
  const [availableImages, setAvailableImages] = useState([]);
  const [isOpenActiveFileSelect, setIsOpenActiveFileSelect] = useState(false);
  const [isOpenDisabledFileSelect, setIsOpenDisabledFileSelect] = useState(false);
  const [startsAt, setStartsAt] = useState<any>();
  const [expiresAt, setExpiresAt] = useState<any>();
  
  useEffect(() => {
    (async () => {
      setLeaguesList(await getAvailableLeagues());
      setLevels(await getLevels());
      setAvailableImages(await getImagesList({ type: 'GROUP_COVER', take: 50 }));
    })();
  }, []);
  
  useEffect(() => {
    if (selectedCompetition?.id) {
      setNewGroupForm({
        id: selectedCompetition?.id,
        name: selectedCompetition?.name || '',
        description: selectedCompetition?.description || '',
        isPaid: selectedCompetition?.isPaid || false,
        price: selectedCompetition?.price || 0,
        levelId: selectedCompetition?.levelId || '',
        imageId: selectedCompetition?.image?.id || null,
        imageDisabledId: selectedCompetition?.imageDisabled?.id || null,
        leagues: selectedCompetition?.leagues || []
      });
      setSelectedLeagues(selectedCompetition.leagues?.map((item: any) => item?.id));
      setImagePreview(selectedCompetition?.image?.url || '');
      setDisabledImagePreview(selectedCompetition?.imageDisabled?.url || '');
      setStartsAt(selectedCompetition?.startsAt ? dayjs(selectedCompetition?.startsAt) : undefined);
      setExpiresAt(selectedCompetition?.expiresAt ? dayjs(selectedCompetition?.expiresAt) : undefined);
    } else {
      setSelectedLeagues([]);
    }
  }, [selectedCompetition]);
  
  useEffect(() => {
    if (newGroupForm.name.length > 5 && selectedLeagues.length > 0) {
      if (newGroupForm.isPaid && newGroupForm.price > 0) {
        setIsValidFormData(!!startsAt && !!expiresAt);
      } else if (!newGroupForm.isPaid && newGroupForm?.levelId?.length > 0) {
        setIsValidFormData(!!startsAt && !!expiresAt);
      } else {
        setIsValidFormData(false);
      }
    } else {
      setIsValidFormData(false);
    }
  }, [newGroupForm, selectedLeagues, startsAt, expiresAt]);
  
  const handleCreateNewGroup = async () => {
    try {
      const updatedSelectedLeagues = selectedLeagues.map((item: any) => ({ leagueId: item }));
      const config = {
        ...newGroupForm,
        leagues: updatedSelectedLeagues,
        levelId: newGroupForm.levelId,
        startsAt: startsAt?.format('YYYY-MM-DDTHH:mm:ss.SSS[Z]'),
        expiresAt: expiresAt?.format('YYYY-MM-DDTHH:mm:ss.SSS[Z]'),
      };
      
      if (config.isPaid) {
        config.levelId = null;
      } else if (!config.isPaid) {
        config.price = null;
      }
      
      if (selectedCompetition) {
        await updateGroup(config, newGroupForm.id);
      } else {
        await createNewGroup(config);
      }
      setOpenSnackbar({
        isOpen: true,
        message: `${selectedCompetition ? "Competition" : "New competition"} has been successfully ${selectedCompetition ? "updated" : "created"}`,
        alertType: 'success'
      });
      handleCloseNewGroupDialog(true);
      handleOnUpdate();
    } catch (e) {
      setOpenSnackbar({
        isOpen: true,
        message: 'Something went wrong, the new competition was not created, please check the correctness of the data and try again',
        alertType: 'error'
      });
    }
  };
  
  const handleIsValidImage = (url: string) => {
    return /\.(jpg|jpeg|png|webp|avif|gif)$/.test(url);
  };
  
  const close = () => {
    setNewGroupForm({
      name: '',
      description: '',
      isPaid: false,
      price: 0,
      levelId: '',
      imageId: null,
      imageDisabledId: null,
      leagues: []
    });
    handleCloseNewGroupDialog();
    setIsValidFormData(false);
    setOpenSnackbar({...openSnackbar, isOpen: false});
    setImagePreview('');
    setDisabledImagePreview('');
    setStartsAt(undefined);
    setExpiresAt(undefined);
  };
  
  const handleUploadImage = async (files: any) => {
    try {
      const preSignedImage = await createPreSignedUrl(
        { type: "GROUP_COVER", name: files[0].name, isPublic: true}
      );
      
      if (preSignedImage.id) setNewGroupForm({...newGroupForm, imageId: preSignedImage.id});
      
      await fetch(preSignedImage.presignedUrl, {
        method: "PUT",
        body: files[0],
      });
      setImagePreview(preSignedImage.url);
      setOpenSnackbar({
        isOpen: true,
        message: `Active image has been successfully uploaded`,
        alertType: 'success'
      });
    } catch (e) {
      setOpenSnackbar({
        isOpen: true,
        message: `Something went wrong, the Active image was not uploaded, please try again or other image`,
        alertType: 'error'
      });
    }
  }
  
  const handleUploadDisabledImage = async (files: any) => {
    try {
      const preSignedImage = await createPreSignedUrl(
        { type: "GROUP_COVER", name: files[0].name, isPublic: true}
      );
      
      if (preSignedImage.id) setNewGroupForm({...newGroupForm, imageDisabledId: preSignedImage.id});
      
      await fetch(preSignedImage.presignedUrl, {
        method: "PUT",
        body: files[0],
      });
      setDisabledImagePreview(preSignedImage.url);
      setOpenSnackbar({
        isOpen: true,
        message: `Disabled image has been successfully uploaded`,
        alertType: 'success'
      });
    } catch (e) {
      setOpenSnackbar({
        isOpen: true,
        message: `Something went wrong, the disabled image was not uploaded, please try again or other image`,
        alertType: 'error'
      });
    }
  }
  
  const handleRemoveImage = async (isActiveImage: boolean) => {
    try {
      const imageId = isActiveImage ? newGroupForm.imageId || newGroupForm.image.id :
        newGroupForm.imageDisabledId || newGroupForm.imageDisabled.id;
      await deleteImage(imageId);
      setNewGroupForm({
        ...newGroupForm,
        ...(isActiveImage && { image: null, imageId: null }),
        ...(!isActiveImage && { imageDisabled: null, imageDisabledId: null }),
      });
      isActiveImage ? setImagePreview('') : setDisabledImagePreview('');
    } catch (e) {
      console.log(e);
    }
  }
  
  const handleSelectActiveImage = (selectImage: ImageType) => {
    setIsOpenActiveFileSelect(false);
    setNewGroupForm({...newGroupForm, imageId: selectImage.id});
    setImagePreview(selectImage.url);
  }
  
  const handleSelectDisabledImage = (selectImage: ImageType) => {
    setIsOpenDisabledFileSelect(false);
    setNewGroupForm({...newGroupForm, imageDisabledId: selectImage.id});
    setDisabledImagePreview(selectImage.url);
  }
  
  return (
    <Dialog open={isOpen} onClose={close} fullWidth maxWidth="md">
      <DialogTitle>{selectedCompetition?.id ? "Update competition" : "Create new competition"}</DialogTitle>
      <DialogContent>
        <DialogContentText className="!mb-5">
          {`Fill in the form and press the ${selectedCompetition?.id ? "'Update competition'" : "'Create new competition'"} button`}
        </DialogContentText>
        <FormControl fullWidth>
          <TextField
            value={newGroupForm.name}
            label="Competition name"
            variant="outlined"
            onChange={(event) =>
              setNewGroupForm({ ...newGroupForm, name: event.target.value})
            }
            required
          />
          <TextField
            value={newGroupForm.description}
            className="!mt-5"
            label="Competition description"
            variant="outlined"
            onChange={(event) =>
              setNewGroupForm({ ...newGroupForm, description: event.target.value})
            }
          />
          <FormControlLabel
            className="!mt-5 w-max"
            control={
              <Switch
                defaultChecked={false}
                onChange={(event) =>
                  setNewGroupForm({...newGroupForm, isPaid: event.target.checked})
                }
              />
            }
            label="Is paid"
          />
          {newGroupForm.isPaid && (
            <TextField
              className="!mt-5"
              label="Price"
              variant="outlined"
              value={newGroupForm.price}
              onChange={(event) =>
                setNewGroupForm({ ...newGroupForm, price: Number(event.target.value)})
              }
              required
            />
          )}
          {!newGroupForm.isPaid && (
            <FormControl fullWidth className="!mt-5">
              <InputLabel id="select-level-helper-label" className="px-10" required>Select level</InputLabel>
              <Select
                labelId="select-level-helper-label"
                id="simple-select"
                value={newGroupForm.levelId}
                label="Select level"
                placeholder="Select level"
                onChange={(event) =>
                  setNewGroupForm({ ...newGroupForm, levelId: event.target.value})
                }
              >
                {levels.map((item: any, index: any) => <MenuItem value={item.id} key={`levelItem-${item.id}-${index}`}>{item.name}</MenuItem>)}
              </Select>
            </FormControl>
          )}
          <FormControl fullWidth className="!mt-5">
            <InputLabel id="select-leagues-helper-label" className="px-10" required>Select league</InputLabel>
            <Select
              labelId="select-leagues-helper-label"
              id="simple-select-leagues"
              value={selectedLeagues}
              label="Select league"
              placeholder="Select league"
              multiple
              onChange={(event) => setSelectedLeagues(event.target.value)}
            >
              {leaguesList.map((item: any, index: any) => <MenuItem value={item.id} key={`leaguesList-Item-${item.id}-${index}`}>{item.name}</MenuItem>)}
            </Select>
          </FormControl>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <div className="flex items-start justify-between">
              <FormControl fullWidth className="!mt-5">
                <DatePicker label="Set start date *" value={startsAt} onChange={(newValue) => setStartsAt(newValue)} />
              </FormControl>
              <FormControl fullWidth className="!mt-5 !ml-5">
                <DatePicker label="Set expired date *" value={expiresAt} onChange={(newValue) => setExpiresAt(newValue)} />
              </FormControl>
            </div>
          </LocalizationProvider>
        </FormControl>
        <div>
          {(imagePreview || disabledImagePreview) &&
						<div className="flex items-start justify-between">
              {imagePreview && (
                <div className="flex-col w-max">
                  <label
                    className="flex items-center justify-center my-2 text-sm text-gray"
                  >
                    Preview active image
                  </label>
                  <img src={imagePreview} alt="image-preview" className="w-[40%] h-auto my-0 mx-auto" />
                </div>
              )}
              {disabledImagePreview && (
                <div className="flex-col w-max">
                  <label
                    className="flex items-center my-2 justify-center text-sm text-gray"
                  >
                    Preview disabled image
                  </label>
                  <img src={disabledImagePreview} alt="disabled-image-preview" className="w-[40%] h-auto my-0 mx-auto" />
                </div>
              )}
						</div>
          }
          {(imagePreview || disabledImagePreview) && (
            <div className="mt-5 flex justify-between">
              {imagePreview && (
                <Button variant="outlined" color="error" onClick={() => handleRemoveImage(true)}>
                  Remove active image
                </Button>
              )}
              {disabledImagePreview && (
                <Button variant="outlined" color="error" onClick={() => handleRemoveImage(false)}>
                  Remove disabled image
                </Button>
              )}
            </div>
          )}
          <input
            accept="image/*"
            style={{ display: "none" }}
            id="active-button-file"
            multiple
            type="file"
            onChange={(event) =>
              handleUploadImage(event.target.files)
            }
          />
          <input
            accept="image/*"
            style={{ display: "none" }}
            id="disabled-button-file"
            multiple
            type="file"
            onChange={(event) =>
              handleUploadDisabledImage(event.target.files)
            }
          />
          <div className="flex items-center justify-between mt-5">
            <div className="w-full">
              <FormControl fullWidth>
                <InputLabel id="select-active-file">Select active image</InputLabel>
                <Select
                  labelId="select-active-file"
                  id="select-active-file-component"
                  label="Select active image"
                  open={isOpenActiveFileSelect}
                  onOpen={() => setIsOpenActiveFileSelect(true)}
                  onClose={() => setIsOpenActiveFileSelect(false)}
                >
                  <Grid container spacing={0} className="max-w-[550px] max-h-[150px] overflow-auto">
                    {availableImages.map((item: any, index: any) => (
                      <Grid item md={0} key={`active-availableImages-Grid-${index}`}>
                        <MenuItem value={item.id} onClick={() => handleSelectActiveImage(item)}>
                          <Avatar variant="rounded" src={item.url}>
                            <ImageTwoToneIcon />
                          </Avatar>
                          <span>{item.label}</span>
                        </MenuItem>
                      </Grid>
                    ))}
                  </Grid>
                </Select>
              </FormControl>
              <p className="mt-2 mb-2 text-xs w-full text-center font-bold text-gray">OR</p>
              <label
                htmlFor="active-button-file"
                className="flex items-center justify-center p-3 rounded-md bg-metal text-white w-full text-center cursor-pointer"
              >
                Upload active image
              </label>
            </div>
            <div className="w-full ml-5">
              <FormControl fullWidth>
                <InputLabel id="select-disabled-file">Select disabled image</InputLabel>
                <Select
                  labelId="select-disabled-file"
                  id="select-disabled-file-component"
                  label="Select disabled image"
                  open={isOpenDisabledFileSelect}
                  onOpen={() => setIsOpenDisabledFileSelect(true)}
                  onClose={() => setIsOpenDisabledFileSelect(false)}
                >
                  <Grid container spacing={0} className="max-w-[550px] overflow-auto max-h-[150px]">
                    {availableImages.map((item: any, index: any) => (
                      <Grid item md={0} key={`disabled-availableImages-Grid-${index}`}>
                        <MenuItem value={item.id} onClick={() => handleSelectDisabledImage(item)}>
                          <Avatar variant="rounded" src={item.url}>
                            <ImageTwoToneIcon />
                          </Avatar>
                          <span>{item.label}</span>
                        </MenuItem>
                      </Grid>
                    ))}
                  </Grid>
                </Select>
              </FormControl>
              <p className="mt-2 mb-2 text-xs w-full text-center font-bold text-gray">OR</p>
              <label
                htmlFor="disabled-button-file"
                className="flex items-center justify-center p-3 rounded-md bg-metal text-white w-full text-center cursor-pointer"
              >
                Upload disabled image
              </label>
            </div>
          </div>
        </div>
        {handleIsValidImage(newGroupForm.imageUrl) && (
          <img src={newGroupForm.imageUrl} alt={newGroupForm.imageUrl} className="w-[200px] h-auto mt-5" />
        )}
        <div className="flex justify-between content-center mt-5">
          <Button variant="outlined" onClick={close}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleCreateNewGroup} disabled={!isValidFormData}>
            {selectedCompetition ? "Update competition" : "Create new competition"}
          </Button>
        </div>
      </DialogContent>
      <Snackbar
        open={openSnackbar.isOpen}
        autoHideDuration={5000}
        onClose={() => setOpenSnackbar({...openSnackbar, isOpen: false})}
      >
        <MuiAlert
          onClose={() => setOpenSnackbar({...openSnackbar, isOpen: false})}
          severity={openSnackbar.alertType}
          sx={{ width: '100%' }}
        >
          {openSnackbar.message}
        </MuiAlert>
      </Snackbar>
    </Dialog>
  )
};

export default NewCompetitionsDialog;